import React, { useCallback, useRef } from 'react';
import './CodeEditor.css';

const INDENT = '  ';

/**
 * CodeEditor — monospace `<textarea>` with code-editor niceties.
 *
 * Behaviours layered on a plain textarea:
 *   - `Tab` inserts two spaces at the cursor instead of advancing focus.
 *   - `Shift+Tab` strips up to two leading spaces from the current line.
 *   - `Enter` carries the previous line's leading whitespace forward
 *     (auto-indent).
 *   - `Cmd/Ctrl+Enter` calls `onSubmit` so the user can fire-and-forget
 *     without leaving the editor.
 *
 * Auto-resize is intentionally OUT of scope; the design pins a 150px min height.
 *
 * Props:
 *  - value        string
 *  - onChange     (next: string) => void
 *  - placeholder  ?string
 *  - autoFocus    ?bool
 *  - onSubmit     ?() => void — invoked on Cmd/Ctrl+Enter
 *  - id           ?string — for label `htmlFor`
 *  - ariaLabel    ?string — accessible name (defaults to "Code editor")
 */
function CodeEditor({
  value = '',
  onChange,
  placeholder = '',
  autoFocus = false,
  onSubmit,
  id,
  ariaLabel = 'Code editor',
}) {
  const ref = useRef(null);

  // Helper: mutate the textarea value via setRangeText-equivalent semantics
  // and notify the parent. We avoid `document.execCommand('insertText', …)`
  // because jsdom's implementation is incomplete and inconsistent across
  // browsers.
  const replaceRange = useCallback(
    (start, end, replacement, newCursor) => {
      const el = ref.current;
      if (!el) return;
      const next = el.value.slice(0, start) + replacement + el.value.slice(end);
      if (typeof onChange === 'function') onChange(next);
      // Defer cursor placement to after React applies the new value. We set
      // it on the element directly; React keeps the DOM value in sync with
      // `value` on the next render.
      requestAnimationFrame(() => {
        const node = ref.current;
        if (!node) return;
        try {
          node.selectionStart = newCursor;
          node.selectionEnd = newCursor;
        } catch {
          /* setSelectionRange may throw on non-text inputs */
        }
      });
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event) => {
      const el = ref.current;
      if (!el) return;

      // Cmd/Ctrl+Enter → submit
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        if (typeof onSubmit === 'function') {
          event.preventDefault();
          onSubmit();
        }
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        const { selectionStart, selectionEnd, value: text } = el;
        if (event.shiftKey) {
          // Find start of current line.
          const lineStart = text.lastIndexOf('\n', selectionStart - 1) + 1;
          const lead = text.slice(lineStart, lineStart + INDENT.length);
          if (lead === INDENT) {
            const cursor = Math.max(lineStart, selectionStart - INDENT.length);
            replaceRange(lineStart, lineStart + INDENT.length, '', cursor);
          } else if (lead.startsWith(' ')) {
            // Strip a single leading space if a full indent isn't present.
            const cursor = Math.max(lineStart, selectionStart - 1);
            replaceRange(lineStart, lineStart + 1, '', cursor);
          }
        } else {
          replaceRange(
            selectionStart,
            selectionEnd,
            INDENT,
            selectionStart + INDENT.length,
          );
        }
        return;
      }

      // Enter — autoindent: copy the previous line's leading whitespace.
      // We do this in keydown rather than letting the default fire and patching
      // afterward so the cursor lands correctly without a visible jump.
      if (event.key === 'Enter' && !event.shiftKey && !event.altKey) {
        const { selectionStart, selectionEnd, value: text } = el;
        if (selectionStart !== selectionEnd) return; // let multi-select fall through
        const lineStart = text.lastIndexOf('\n', selectionStart - 1) + 1;
        const lineToCursor = text.slice(lineStart, selectionStart);
        const indentMatch = lineToCursor.match(/^[\t ]*/);
        const indent = indentMatch ? indentMatch[0] : '';
        if (!indent) return; // no indent — let native Enter handle it
        event.preventDefault();
        const insert = `\n${indent}`;
        replaceRange(
          selectionStart,
          selectionEnd,
          insert,
          selectionStart + insert.length,
        );
      }
    },
    [onSubmit, replaceRange],
  );

  const handleChange = useCallback(
    (event) => {
      if (typeof onChange === 'function') onChange(event.target.value);
    },
    [onChange],
  );

  return (
    <textarea
      ref={ref}
      id={id}
      className="code-editor"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      spellCheck="false"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      wrap="off"
    />
  );
}

export default CodeEditor;
